/**
 * src/lib/connection-status.js
 *
 * Tracks transport-layer health: whether callables are working (bootstrap
 * succeeds), separate from whether the session role resolved. A timeout or
 * error in bootstrap is now classified as a callable failure, not a blanket
 * "offline" state — callables are the portal's primary path and are
 * 503-immune at me-central2.
 *
 * States:
 * - 'online': callables working, session fully resolved
 * - 'limited': callables working but slow/degraded
 * - 'denied': the backend answered DEFINITIVELY with permission-denied /
 *   unauthenticated / failed-precondition (see api.js's guardedRead denied
 *   classification) — this is proof the infrastructure IS up and reachable;
 *   it is NEVER shown as the alarming 'offline' badge (FIX 9, 2026-07-11 —
 *   the motivating case is a role-gated screen a caller's role legitimately
 *   can't see, which is not a connectivity problem at all).
 * - 'offline': callable/auth infrastructure truly unreachable (no result at
 *   all, or a non-auth transport error). Timeout alone is 'limited', not
 *   'offline' — a slow/degraded callable isn't the same failure as one
 *   that's actually down.
 */

import { t } from '../i18n/t.js';

let lastStatus = 'online';
let lastError = null;

/**
 * Call this after the bootstrap result is known to update connection state.
 * @param {{data, timedOut, error, denied}} result — from guardedRead
 * @returns {string} — 'online' | 'limited' | 'denied' | 'offline'
 */
export function updateConnectionStatus(result) {
  if (!result) {
    lastStatus = 'offline';
    lastError = null;
    return lastStatus;
  }

  // Permission-denied / unauthenticated → 'denied', NOT 'offline'. The
  // backend answered — role check ran and said no, or the auth token is
  // invalid — that's proof the infrastructure IS up; showing the same
  // alarming "Offline" badge a genuine outage gets would be actively
  // misleading (e.g. a manager landing on an admin-only screen).
  if (result.denied) {
    lastStatus = 'denied';
    lastError = result.error;
    return lastStatus;
  }

  // Timeout but no auth error → callables working but slow (limited connectivity)
  if (result.timedOut) {
    lastStatus = 'limited';
    lastError = null;
    return lastStatus;
  }

  // Transport/network error (not auth, not a timeout) → offline (infrastructure
  // truly unreachable) — the only failure branch that earns the 'offline' badge.
  if (result.error) {
    lastStatus = 'offline';
    lastError = result.error;
    return lastStatus;
  }

  // Success
  lastStatus = 'online';
  lastError = null;
  return lastStatus;
}

/**
 * @returns {string} current connection status
 */
export function getConnectionStatus() {
  return lastStatus;
}

/**
 * @returns {any} last error, if any
 */
export function getLastConnectionError() {
  return lastError;
}

/**
 * Returns the badge text for the header, reflecting actual connection state.
 * - 'online': show nothing or a subtle live dot
 * - 'limited': show "limited" or a small note
 * - 'denied': show a neutral access note — NOT the alarming "offline" text
 * - 'offline': show "offline"
 *
 * @returns {string} — badge text or empty for 'online'
 */
export function getConnectionBadgeText() {
  switch (lastStatus) {
    case 'online':
      return ''; // No scary badge — callables are working fine
    case 'limited':
      return t('connection.limited'); // Neutral note that something is degraded
    case 'denied':
      return t('connection.restricted'); // Definitive denial — backend is alive, not a connectivity problem
    case 'offline':
      return t('connection.offline'); // Everything is down
    default:
      return '';
  }
}
